import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Instagram } from "lucide-react";
import { IMAGES, CATEGORIES, BOXES } from "../lib/site";
import WaitlistForm from "../components/site/WaitlistForm";

export default function Home() {
  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section data-testid="hero-section" className="relative min-h-[100vh] flex items-end overflow-hidden">
        <img
          src={IMAGES.hero}
          alt="Luxury self-care routine"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 overlay-dark" />
        <div className="relative z-10 w-full max-w-[1440px] mx-auto px-6 md:px-12 pb-24 md:pb-32">
          <div className="max-w-3xl fade-up">
            <div className="text-[11px] uppercase tracking-luxe text-gold mb-6">Crowned Essence · Est. 2025</div>
            <h1 className="font-serif text-white text-5xl md:text-7xl lg:text-[88px] leading-[0.95] tracking-tight">
              Luxury Self-Care.<br/>
              <span className="italic text-gold">Curated with Purpose.</span>
            </h1>
            <p className="mt-8 text-lg md:text-xl text-white/85 max-w-xl font-light leading-relaxed">
              Discover premium Black-owned haircare, skincare, beard care, and self-care essentials — delivered to your door.
            </p>
            <div className="mt-10 flex flex-wrap gap-4">
              <Link data-testid="hero-cta-waitlist" to="/waitlist" className="px-8 py-4 bg-gold text-matte text-[11px] uppercase tracking-luxe-sm hover:bg-gold-soft transition-colors">
                Join the Waitlist
              </Link>
              <Link data-testid="hero-cta-partner" to="/partnerships" className="px-8 py-4 border border-white/50 text-white text-[11px] uppercase tracking-luxe-sm hover:bg-white/10 transition-colors">
                Partner With Us
              </Link>
            </div>
          </div>
        </div>
        <div className="absolute bottom-8 right-8 z-10 text-white/70 text-[10px] uppercase tracking-luxe hidden md:block">
          Scroll to explore ↓
        </div>
      </section>

      {/* FEATURED CATEGORIES */}
      <section data-testid="categories-section" className="py-24 md:py-32 bg-cream">
        <div className="max-w-[1440px] mx-auto px-6 md:px-12">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-16">
            <div>
              <div className="text-[11px] uppercase tracking-luxe text-gold mb-4">The Collection</div>
              <h2 className="font-serif text-4xl md:text-6xl max-w-2xl leading-[1.05]">Five worlds of routine, one crown.</h2>
            </div>
            <p className="max-w-sm text-matte/70 text-base leading-relaxed">Every category is meticulously curated to honor exceptional Black-owned brands and elevate your everyday.</p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 md:gap-6">
            {CATEGORIES.map((c, i) => (
              <Link
                to="/boxes"
                key={c.key}
                data-testid={`category-card-${c.key}`}
                className={`group relative overflow-hidden h-72 md:h-96 ${i === 0 ? "col-span-2 md:col-span-2 lg:col-span-3 lg:row-span-2 md:h-[calc(24rem+1.5rem)] lg:h-auto" : ""}`}
              >
                <img src={c.img} alt={c.label} className="absolute inset-0 w-full h-full object-cover transition-transform duration-[1200ms] group-hover:scale-105" />
                <div className="absolute inset-0 bg-gradient-to-t from-matte/85 via-matte/20 to-transparent" />
                <div className="relative z-10 h-full flex flex-col justify-end p-6">
                  <div className="text-[10px] uppercase tracking-luxe text-gold mb-2">Category</div>
                  <div className="font-serif text-2xl md:text-3xl text-white">{c.label}</div>
                  <div className="text-white/70 text-sm mt-1">{c.blurb}</div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* MISSION */}
      <section data-testid="mission-section" className="py-24 md:py-40 bg-ivory">
        <div className="max-w-[1440px] mx-auto px-6 md:px-12 grid md:grid-cols-2 gap-12 lg:gap-24 items-center">
          <div className="relative aspect-[4/5] overflow-hidden">
            <img src={IMAGES.mission} alt="Mission" className="absolute inset-0 w-full h-full object-cover" />
          </div>
          <div>
            <div className="text-[11px] uppercase tracking-luxe text-gold mb-6">Our Mission</div>
            <h2 className="font-serif text-4xl md:text-6xl leading-[1.05]">More than a<br/><span className="italic">subscription.</span></h2>
            <div className="gold-divider my-8" />
            <p className="text-lg text-matte/80 leading-relaxed font-light">
              Crowned Essence exists to celebrate Black-owned excellence by creating a premium subscription experience that introduces customers to exceptional brands — while helping those businesses grow.
            </p>
            <p className="mt-6 text-base text-matte/60 leading-relaxed">
              Every box is a discovery. Every routine, a small act of luxury. Every purchase, a vote for community, craft, and culture.
            </p>
            <Link to="/about" data-testid="mission-learn-more" className="mt-8 inline-flex items-center gap-2 text-[11px] uppercase tracking-luxe-sm text-matte hover:text-gold transition-colors">
              Read Our Story <ArrowRight size={14}/>
            </Link>
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section data-testid="how-it-works-section" className="py-24 md:py-32 bg-cream-warm">
        <div className="max-w-[1440px] mx-auto px-6 md:px-12">
          <div className="text-center max-w-2xl mx-auto mb-16">
            <div className="text-[11px] uppercase tracking-luxe text-gold mb-4">The Routine</div>
            <h2 className="font-serif text-4xl md:text-6xl leading-[1.05]">How it works.</h2>
          </div>
          <div className="grid md:grid-cols-3 gap-8 md:gap-16">
            {[
              { n: "01", t: "Join the Waitlist", d: "Secure your seat at the crown table and unlock early access to our founding boxes." },
              { n: "02", t: "Receive a Curated Box", d: "Each month, an intentionally curated collection of premium Black-owned essentials arrives at your door." },
              { n: "03", t: "Discover New Favorites", d: "Fall in love with new brands. Support Black-owned excellence. Elevate your routine." },
            ].map((step) => (
              <div key={step.n} data-testid={`step-${step.n}`} className="relative">
                <div className="font-serif italic text-gold text-7xl leading-none">{step.n}</div>
                <div className="gold-divider mt-6 mb-6" />
                <h3 className="font-serif text-2xl md:text-3xl">{step.t}</h3>
                <p className="mt-3 text-matte/70 leading-relaxed">{step.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* BOXES */}
      <section data-testid="boxes-section" className="py-24 md:py-32 bg-matte text-ivory">
        <div className="max-w-[1440px] mx-auto px-6 md:px-12">
          <div className="mb-16">
            <div className="text-[11px] uppercase tracking-luxe text-gold mb-4">The Boxes</div>
            <h2 className="font-serif text-4xl md:text-6xl leading-[1.05] max-w-3xl">Three signature editions.<br/><span className="italic text-gold">Coming soon.</span></h2>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            {BOXES.map((b) => (
              <div key={b.name} data-testid={`box-${b.name.toLowerCase().replace(/[^a-z]+/g,'-')}`} className="group border border-ivory/15 hover:border-gold transition-colors">
                <div className="relative aspect-[4/5] overflow-hidden">
                  <img src={b.img} alt={b.name} className="w-full h-full object-cover transition-transform duration-[1200ms] group-hover:scale-105" />
                  <div className="absolute top-4 left-4 px-3 py-1 bg-ivory text-matte text-[10px] uppercase tracking-luxe-sm">Coming Soon</div>
                </div>
                <div className="p-8">
                  <div className="text-[10px] uppercase tracking-luxe text-gold">{b.tag}</div>
                  <h3 className="font-serif text-3xl mt-2">{b.name}</h3>
                  <p className="mt-4 text-ivory/60 leading-relaxed text-sm">{b.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* PARTNERSHIP */}
      <section data-testid="partnership-section" className="py-24 md:py-40 bg-ivory">
        <div className="max-w-[1440px] mx-auto px-6 md:px-12 grid md:grid-cols-2 gap-12 items-center">
          <div>
            <div className="text-[11px] uppercase tracking-luxe text-gold mb-6">Brand Partnerships</div>
            <h2 className="font-serif text-4xl md:text-6xl leading-[1.05]">Calling all<br/><span className="italic">Black-Owned Brands.</span></h2>
            <div className="gold-divider my-8" />
            <p className="text-lg text-matte/80 leading-relaxed font-light max-w-xl">
              We're looking to partner with exceptional Black-owned haircare, skincare, beard care and accessory brands. Let's build something legacy-worthy — together.
            </p>
            <Link to="/partnerships" data-testid="partnership-cta" className="mt-8 inline-block px-10 py-4 bg-matte text-gold border border-gold/40 text-[11px] uppercase tracking-luxe-sm hover:bg-black transition-colors">
              Become a Partner
            </Link>
          </div>
          <div className="relative aspect-square overflow-hidden">
            <img src={IMAGES.products} alt="Brand partners" className="absolute inset-0 w-full h-full object-cover" />
          </div>
        </div>
      </section>

      {/* WAITLIST */}
      <section data-testid="waitlist-section" className="py-24 md:py-40 bg-matte text-ivory relative overflow-hidden">
        <div className="max-w-[1440px] mx-auto px-6 md:px-12 grid md:grid-cols-5 gap-12 items-center">
          <div className="md:col-span-3">
            <div className="text-[11px] uppercase tracking-luxe text-gold mb-6">The Waitlist</div>
            <h2 className="font-serif text-4xl md:text-6xl leading-[1.05]">Be first to experience<br/><span className="italic text-gold">Crowned Essence.</span></h2>
            <p className="mt-6 text-lg text-ivory/70 leading-relaxed max-w-xl font-light">
              Join our community and be the first to know when we launch. Early members receive founding-tier pricing and exclusive box editions.
            </p>
          </div>
          <div className="md:col-span-2">
            <WaitlistForm variant="dark" testIdPrefix="home-waitlist" />
          </div>
        </div>
      </section>

      {/* INSTAGRAM */}
      <section data-testid="instagram-section" className="py-24 md:py-32 bg-cream">
        <div className="max-w-[1440px] mx-auto px-6 md:px-12">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-12">
            <div>
              <div className="text-[11px] uppercase tracking-luxe text-gold mb-4">The Feed</div>
              <h2 className="font-serif text-4xl md:text-5xl">@crownedessence5</h2>
            </div>
            <a data-testid="instagram-follow" href="https://instagram.com/crownedessence5" target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 text-[11px] uppercase tracking-luxe-sm text-matte hover:text-gold transition-colors">
              <Instagram size={14} /> Follow us
            </a>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {IMAGES.ig.map((src, i) => (
              <a key={i} data-testid={`ig-tile-${i}`} href="https://instagram.com/crownedessence5" target="_blank" rel="noreferrer" className="relative aspect-square overflow-hidden group">
                <img src={src} alt="Instagram" className="w-full h-full object-cover transition-transform duration-[1200ms] group-hover:scale-105" />
                <div className="absolute inset-0 bg-matte/0 group-hover:bg-matte/40 transition-colors flex items-center justify-center">
                  <Instagram className="text-gold opacity-0 group-hover:opacity-100 transition-opacity" size={22} />
                </div>
              </a>
            ))}
          </div>
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section data-testid="testimonials-section" className="py-24 md:py-32 bg-ivory">
        <div className="max-w-[1440px] mx-auto px-6 md:px-12">
          <div className="text-center max-w-2xl mx-auto mb-16">
            <div className="text-[11px] uppercase tracking-luxe text-gold mb-4">Voices of the Crown</div>
            <h2 className="font-serif text-4xl md:text-6xl leading-[1.05]">Coming soon.</h2>
            <p className="mt-6 text-matte/60">Our founding members' stories will live here — real routines, real reviews.</p>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            {[1,2,3].map(i => (
              <div key={i} data-testid={`testimonial-placeholder-${i}`} className="border border-taupe/40 p-10 bg-cream">
                <div className="font-serif italic text-2xl text-matte/40">"Your voice, coming soon."</div>
                <div className="gold-divider mt-6 mb-4" />
                <div className="text-[11px] uppercase tracking-luxe-sm text-matte/50">Founding Member</div>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
